// Shared data-grid height fit. Extracted from TabOfDataTable so pages that build their grid
// without the hub (e.g. Planning > Reporting) get the same behaviour instead of a copy.
// Imports none; the shell is a plain DOM element, so everything here is DOM-typed.

// Fit the list so it scrolls internally (sticky header at top, pagination just below) with no
// page-level scroll or grey gap — or, when the list is empty / loading, fill the .empty-state /
// .loading block so its content centers in the full area. Applies to full-page lists (rendered as
// a direct child of .contentwrapper) AND to lists embedded in the detail popup (.verticalScroll),
// where it fits the popup's scroll area instead of the viewport. Other embedded tables are left at
// natural height. Reads real geometry, so it is immune to the legacy .MenuButtonList overlay hack
// that breaks a pure-CSS flex chain, and to the varied admin-page wrappers inside the popup.
// Bounding the height is also what makes a wide grid usable sideways: the horizontal scrollbar of
// .table-wrap then sits at the bottom of the viewport instead of below a page-tall table.
function fitGridHeight(shell: HTMLElement | null): void {
  if (!shell) return;
  // In the detail popup the bounding scroll area is .verticalScroll; on full pages it is the
  // viewport. A .grid-fit-bounds ancestor bounds the fit tighter than either — used by pages
  // that stack several grids (e.g. Fraud Hit Details' half-and-half split), where each grid
  // must fill its pane instead of the viewport. Bail for any other embedded context (no popup,
  // no bounds, not a full-page .contentwrapper list).
  const fitBounds = shell.closest('.grid-fit-bounds') as HTMLElement | null;
  const popupScroller = shell.closest('.verticalScroll') as HTMLElement | null;
  const inContentWrapper = !!shell.parentElement && shell.parentElement.classList.contains('contentwrapper');
  if (!fitBounds && !popupScroller && !inContentWrapper) return;
  const target = (shell.querySelector('.table-wrap') ||
    shell.querySelector('.empty-state') ||
    shell.querySelector('.loading')) as HTMLElement | null;
  if (!target) return;
  // Re-measuring drops the fixed height, which momentarily removes the internal overflow and
  // makes the layout flush below clamp scrollTop to 0. This runs on every re-render
  // (e.g. a row-checkbox click), so without restoring scrollTop at the end a scrolled list
  // snaps back to the top on any re-render.
  const scrollTop = target.scrollTop;
  target.style.height = '';
  const targetTop = target.getBoundingClientRect().top;
  const below = shell.getBoundingClientRect().bottom - target.getBoundingClientRect().bottom;
  // Math.floor so sub-pixel getBoundingClientRect values can't push the shell 1px past the
  // bottom reference (which would show a scrollbar on the empty-state / loading block, which has
  // no internal overflow of its own). `below` already includes the pagination foot, so it stays
  // visible just under the fitted table.
  const bottomRef = fitBounds
    ? fitBounds.getBoundingClientRect().bottom
    : popupScroller
    ? popupScroller.getBoundingClientRect().bottom
    : window.innerHeight;
  const available = Math.floor(bottomRef - targetTop - below);
  if (available > 160) {
    target.style.height = `${available}px`;
    // The pane, not the page, is a bounded grid's limit — skip the page-level overflow
    // self-correct below, which would wrongly shrink this grid while a sibling grid is
    // still unfitted. When a bounded grid's height changes it shifts everything below it;
    // the hosting page keeps siblings in sync with a ResizeObserver on the pane that
    // dispatches a window resize (see FraudHitDetails / FRONTEND.md).
    if (!fitBounds) {
      // Self-correct for any residual vertical overflow of the scroll container — the popup's
      // .verticalScroll, or on full pages the legacy .contentwrapper (position:relative; top:80px)
      // adds ~80px on tabbed pages, which an exact fill would otherwise expose as a scrollbar.
      const scroller = popupScroller || (shell.closest('.Basecontent') as HTMLElement | null);
      if (scroller) {
        const overflow = scroller.scrollHeight - scroller.clientHeight;
        if (overflow > 0) target.style.height = `${Math.max(160, available - overflow)}px`;
      }
    }
  }
  target.scrollTop = scrollTop;
}

const gridHeightFitHelper = {
  fitGridHeight,
};

export default gridHeightFitHelper;
